/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Component imports
import { Button, Card, Col, Divider, Empty, Form, Input, Row, Select, Spin, Typography, Collapse } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import AvatarFormFieldSelection from '../UI/AvatarFormFieldSelection';
import DeleteOrganizationModal from '../Modals/DeleteOrganizationModal';
import FormCard from './FormCard';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { DATA_RETENTION_OPTIONS, DEFAULT_DELETE_CONFIRMATION_KEYWORD, PERMISSIONS_ORG } from '../../constants/main';
import { useFetchOrganizationQuery, useUpdateOrganizationMutation } from '../../store/organization/organizationApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import useTheme from '../../utils/hooks/useTheme';
import extractMsTeamsDataFromUrl from '../../utils/misc/extractMsTeamsDataFromUrl';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const timezoneOptions = moment.tz.names();

const OrganizationSettingsForm = ({ setHasPendingChanges, hasPendingChanges, isUpdateMode = false, setFormRef }) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const userPermissions = useUserPermissions(orgId);
  const { data: organization, isFetching: isLoadingOrganization, isError: fetchOrganizationError } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const [updateOrganization] = useUpdateOrganizationMutation();
  const { isLoading: isFetchingMediaFiles } = getCurrentQueryState('fetchMediaFiles', { orgId });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    if(form) {
      setFormRef(form);
    }

    return () => {
      setFormRef(null);
    };
  }, [setFormRef, form]);

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const onSubmit = async (values) => {
    const valuesToSubmit = {
      ...values
    };
    valuesToSubmit.id = organization.id;

    try {
      const isSubmitSuccessful = await updateOrganization({ orgId, data: valuesToSubmit }).unwrap();
      if (isSubmitSuccessful) {
        setHasPendingChanges(false);
      }
    } catch (error) {
      //prevent unwrap reject error
    }
  };

  const renderContent = () => {
    if (!organization && isLoadingOrganization) {
      return (
        <Card>
          <Row justify="center">
            <Col>
              <Spin />
            </Col>
          </Row>
        </Card>
      );
    }

    if (organization) {
      return (
        <FormCard
          name="organizationSettingsForm"
          layout="vertical"
          labelCol={{ xs: 24, md: 10, xl: 8, xxl: 6 }}
          wrapperCol={{ xs: 24, md: 18, xl: 14, xxl: 12 }}
          onFinish={onSubmit}
          form={form}
          initialValues={organization}
          onValuesChange={(_, allFields) => setHasPendingChanges?.(true)}
          onFieldsChange={(_, allFields) => updateState(allFields)}
          footerActions={[
            isUpdateMode && (
              <Button
                key="submit"
                type="primary"
                size="large"
                disabled={isLoadingOrganization || isFetchingMediaFiles || !hasPendingChanges}
                onClick={() => form.submit()}
              >
                {t('Save Changes')}
              </Button>
            )]}
          footer={isUpdateMode ? (
            <Button
              disabled={isLoadingOrganization || isFetchingMediaFiles || !hasPendingChanges}
              hidden={!isUpdateMode}
              className="float-right"
              type="primary"
              size="large"
              onClick={() => form.submit()}
            >
              {t('Save Changes')}
            </Button>
          ) : null}
        >
          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Configuration Settings')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the details of this organization')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                label={t('Organization Name')}
                name={isUpdateMode && 'displayName'}
              >
                {isUpdateMode ? <Input size="large"/> : organization?.displayName}
              </Form.Item>

              <Form.Item
                label={t('Timezone')}
                name={isUpdateMode && 'timezone'}
              >
                {
                  isUpdateMode ? (
                    <Select
                      size="large"
                      showSearch
                      className="w-100"
                      placeholder={t('Choose a timezone')}
                      showAction={['focus', 'click']}
                      onChange={(value) => {
                        form.setFieldsValue({ timezone: value });
                      }}
                      getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                    >
                      {
                        timezoneOptions.map(timezone => (
                          <Select.Option key={timezone} value={timezone}>{timezone}</Select.Option>
                        ))
                      }
                    </Select>
                  ) : organization?.timezone
                }
              </Form.Item>

              <Form.Item
                label={t('Data Retention')}
                name={isUpdateMode && 'dataRetentionPeriod'}
              >
                {
                  isUpdateMode ? (
                    <Select
                      size="large"
                      className="w-100"
                      placeholder={t('Choose data retention period')}
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                      {
                        DATA_RETENTION_OPTIONS.map((month) => {
                          return (
                            <Select.Option key={month.value} value={month.value}>
                              {month.label}
                            </Select.Option>
                          );
                        })
                      }
                    </Select>
                  ) : (
                    <Fragment>
                      {
                        organization?.dataRetentionPeriod ? (
                          <Fragment>
                            {
                              organization?.dataRetentionPeriod === 1 ? (
                                organization?.dataRetentionPeriod + ` ${t('month')}`
                              ) : (
                                organization?.dataRetentionPeriod + ` ${t('months')}`
                              )
                            }
                          </Fragment>
                        ) : t('No data retention')
                      }
                    </Fragment>
                  )
                }
              </Form.Item>
            </Col>
          </Row>

          <Divider className="mt-2" />

          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Organization Image')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Recommended resolution is 250*250 px, file types: jpg, png')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                name="image"
              >
                <AvatarFormFieldSelection
                  fields={{
                    inputField: null,
                    uploadField: {
                      label: t('Organization Avatar'),
                      validationMessage: t('Please select or upload an image')
                    }
                  }}
                  shouldValidate
                  loading={isFetchingMediaFiles}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider className="mt-2" />

          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('External Organizations')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Connect your Helvia Organization with external Organizations')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              {
                isEmptyArray(organization?.externalOrganizations) && !isUpdateMode ? (
                  <Typography.Text className="fs-sm text-muted">
                    {t('No external organizations')}
                  </Typography.Text>
                ) : (
                  <Form.Item>
                    <Form.List
                      name="externalOrganizations"
                    >
                      {
                        (fields, { add, remove }) => (
                          <Fragment>
                            {
                              fields.map((field, index) => (
                                <Card
                                  className="bg-layout-gray shadow-none position-relative mb-4"
                                  key={field.key}
                                >
                                  <Row>
                                    <Col span={24}>
                                      <Row gutter={16}>
                                        <Col span={24}>
                                          <Form.Item
                                            {...field}
                                            label={t('Organization Type')}
                                            name={[field.name, 'type']}
                                            rules={[{ required: true, message: t('Missing organization type') }]}
                                          >
                                            <Select
                                              size="large"
                                              disabled={!isUpdateMode}
                                              placeholder={t('Select a platform')}
                                              getPopupContainer={triggerNode => triggerNode.parentElement}
                                            >
                                              <Select.Option value="MS_TEAMS_ORGANIZATION">{t('Microsoft Teams')}</Select.Option>
                                            </Select>
                                          </Form.Item>
                                        </Col>

                                        <Col span={24}>
                                          <Form.Item
                                            {...field}
                                            label={t('Azure ID')}
                                            name={[field.name, 'azureTenantId']}
                                            rules={[{
                                              required: true, message: t('Missing Azure Tenant ID'),
                                              validator: async (_rule, value) => {
                                                if (form.getFieldValue('externalOrganizations')) {
                                                  return extractMsTeamsDataFromUrl(value).then(result => {
                                                    form.setFieldsValue({
                                                      externalOrganizations: form.getFieldValue('externalOrganizations').map((item, i) => {
                                                        if (i === index) {
                                                          return {
                                                            ...item,
                                                            azureTenantId: result.azureTenantId
                                                          };
                                                        }
                                                        return item;
                                                      }
                                                      )

                                                    });

                                                    return Promise.resolve();
                                                  }).catch((err) => {
                                                    return Promise.reject();
                                                  });
                                                }
                                              }
                                            }]}
                                          >
                                            <Input
                                              size="large"
                                              disabled={!isUpdateMode}
                                              placeholder="Add ID"
                                            />
                                          </Form.Item>
                                        </Col>
                                      </Row>
                                    </Col>

                                    {
                                      isUpdateMode ? (
                                        <Col span={24}>
                                          <Button
                                            onClick={() => remove(index)}
                                            shape="circle"
                                            type="text"
                                            icon={<DeleteIcon />}
                                            className="bordered border-radius-base float-right"
                                          />
                                        </Col>
                                      ) : null
                                    }
                                  </Row>
                                </Card>
                              ))
                            }

                            <Form.Item>
                              {
                                isUpdateMode ? (
                                  <Button size="large" className="w-100 opacity-50" type="dashed" onClick={() => add()}>
                                    <PlusIcon/> {t('Add Organization')}
                                  </Button>
                                ) : null
                              }
                            </Form.Item>
                          </Fragment>
                        )
                      }
                    </Form.List>
                  </Form.Item>
                )
              }
            </Col>
          </Row>

          {
            userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? (
              <Fragment>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Collapse.Panel
                    showArrow={false}
                    header={
                      <Divider className="fs-base ant-divider-danger" orientation="left">
                        <Typography.Text strong>
                          <span className="title">
                            {t('DANGER ZONE')}
                          </span>
                          <span className="ml-3 icon">
                            <ArrowDownIcon className="fs-base" />
                          </span>
                        </Typography.Text>
                      </Divider>
                    }
                    key="1"
                  >
                    <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
                      <Col span={24}>
                        <Typography.Text className="fs-base text-error">
                          <WarningOutlined className="mr-1 fs-lg mb-4" />  {t('In this section you can perform non reversible actions.')}
                        </Typography.Text>
                      </Col>
                    </Row>

                    <Row gutter={formProps.rowGutter} className="mt-5">
                      <Col xs={formProps.mainColSpan}>
                        <Typography.Text className="fs-base">
                          {t('Organization Deletion')}
                        </Typography.Text>
                        <Typography.Paragraph className="fs-sm" type="secondary">
                          {t('Delete current organization, including all Agents, data, settings, and connections. Users will not be deleted.')}
                        </Typography.Paragraph>
                      </Col>

                      <Col span={formProps.secondaryColSpan}>
                        <Button
                          disabled={isLoadingOrganization || isFetchingMediaFiles}
                          hidden={!isUpdateMode}
                          className="float-left"
                          type="primary"
                          size="large"
                          danger
                          onClick={openDeleteModal}
                        >
                          {`${t('Delete this Organization')}`}
                        </Button>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                </Collapse>

                <DeleteOrganizationModal showDeleteKeyword deleteKeyword={`${DEFAULT_DELETE_CONFIRMATION_KEYWORD.ORGANIZATION} ${organization.displayName?.toUpperCase()}`} isModalOpen={isDeleteModalOpen} closeModal={closeDeleteModal} />
              </Fragment>
            ) : null
          }
        </FormCard>
      );
    }

    if (fetchOrganizationError) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There was an error fetching your data, please refresh your page. If the problem persists, please contact an administrator')}.
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    // else
    return (
      <Empty
        description={
          <Row gutter={16} className="mt-3">
            <Col md={{ span: 12, offset: 6 }}>
              {t('There are no Organization Settings available. Please contact an administrator')}.
            </Col>
          </Row>
        }
        className="mt-3 mb-6"
      />
    );
  };

  return renderContent();
};

export default OrganizationSettingsForm;
